import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Upload, FileText, Trash2, Download, File, Image, X, Brain, AlertTriangle, CheckCircle, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DOC_TYPE_LABELS = {
  sale_deed: "Sale Deed", form_i_xiv: "Form I & XIV", survey_plan: "Survey Plan",
  conversion_sanad: "Conversion Sanad", occupancy_certificate: "Occupancy Certificate",
  noc: "NOC", court_order: "Court Order", mutation_entry: "Mutation Entry",
  tax_receipt: "Tax Receipt", encumbrance_certificate: "Encumbrance Certificate",
  title_search_report: "Title Search Report", power_of_attorney: "Power of Attorney",
  other: "Other Document",
};

export default function PropertyDocuments() {
  const { API } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [filterProp, setFilterProp] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ property_id: "", description: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [intelligence, setIntelligence] = useState(null);
  const [classifying, setClassifying] = useState(null);
  const [showIntelligence, setShowIntelligence] = useState(false);
  const [analyzingAll, setAnalyzingAll] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const query = filterProp !== "all" ? `?property_id=${filterProp}` : "";
      const [dRes, pRes] = await Promise.all([
        fetch(`${API}/documents${query}`, { credentials: "include" }),
        fetch(`${API}/properties`, { credentials: "include" }),
      ]);
      if (dRes.ok) setDocuments(await dRes.json());
      if (pRes.ok) setProperties(await pRes.json());
    } catch (e) { console.error(e); }
  }, [API, filterProp]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const classifyDocument = async (docId) => {
    setClassifying(docId);
    try {
      const res = await fetch(`${API}/documents/classify`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ document_id: docId }),
      });
      if (res.ok) fetchData();
    } catch (e) { console.error(e); }
    setClassifying(null);
  };

  const fetchIntelligence = async (propId) => {
    try {
      const res = await fetch(`${API}/documents/intelligence/${propId}`, { credentials: "include" });
      if (res.ok) {
        setIntelligence(await res.json());
        setShowIntelligence(true);
      }
    } catch (e) { console.error(e); }
  };

  const analyzeAll = async (propId) => {
    setAnalyzingAll(true);
    try {
      await fetch(`${API}/documents/analyze-all/${propId}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      });
      fetchData();
    } catch (e) { console.error(e); }
    setAnalyzingAll(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("File too large. Maximum 5MB allowed.");
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !form.property_id) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(",")[1];
        const res = await fetch(`${API}/documents/upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            property_id: form.property_id,
            filename: selectedFile.name,
            file_type: selectedFile.type || "application/octet-stream",
            file_data: base64,
            description: form.description,
          }),
        });
        if (res.ok) {
          setShowUpload(false);
          setForm({ property_id: "", description: "" });
          setSelectedFile(null);
          fetchData();
        }
        setUploading(false);
      };
      reader.readAsDataURL(selectedFile);
    } catch (e) {
      console.error(e);
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await fetch(`${API}/documents/${docId}`, { method: "DELETE", credentials: "include" });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleDownload = async (doc) => {
    window.open(`${API}/documents/${doc.document_id}/download`, "_blank");
  };

  const getPropertyName = (id) => properties.find((p) => p.property_id === id)?.name || id;
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (type) => {
    if (type?.startsWith("image/")) return Image;
    return FileText;
  };

  return (
    <div data-testid="property-documents-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-white">Property Documents</h2>
          <p className="text-white/40 text-sm font-sans">{documents.length} documents stored</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filterProp} onValueChange={setFilterProp}>
            <SelectTrigger data-testid="filter-property-select" className="w-40 bg-white/5 border-white/10 text-white rounded-sm text-xs">
              <SelectValue placeholder="All Properties" />
            </SelectTrigger>
            <SelectContent className="bg-[#11130F] border-white/10">
              <SelectItem value="all">All Properties</SelectItem>
              {properties.map((p) => (
                <SelectItem key={p.property_id} value={p.property_id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filterProp !== "all" && (
            <>
              <Button data-testid="analyze-intelligence-btn" onClick={() => fetchIntelligence(filterProp)} variant="outline" className="border-white/10 text-white/50 hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm font-sans text-xs">
                <Brain className="w-4 h-4 mr-1" /> Intelligence
              </Button>
              <Button data-testid="analyze-all-btn" onClick={() => analyzeAll(filterProp)} disabled={analyzingAll} variant="outline" className="border-white/10 text-white/50 hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm font-sans text-xs">
                {analyzingAll ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />} Classify All
              </Button>
            </>
          )}
          <Button data-testid="upload-document-btn" onClick={() => setShowUpload(true)} className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans">
            <Upload className="w-4 h-4 mr-2" /> Upload
          </Button>
        </div>
      </div>

      {documents.length === 0 ? (
        <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
          <File className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-sans">No documents uploaded yet. Upload property documents for safekeeping.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => {
            const Icon = getFileIcon(doc.file_type);
            return (
              <div key={doc.document_id} className="bg-[#11130F] border border-white/10 rounded-sm p-4 hover:border-[#D4AF37]/40 transition-colors flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-sm flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="font-sans font-medium text-white text-sm">{doc.filename}</h4>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-white/40 text-[10px] font-sans">{getPropertyName(doc.property_id)}</span>
                      <span className="text-white/20">|</span>
                      <span className="text-white/40 text-[10px] font-sans">{formatSize(doc.file_size)}</span>
                      {doc.ai_classification && doc.ai_classification !== "other" && (
                        <>
                          <span className="text-white/20">|</span>
                          <span className="text-[#D4AF37] text-[9px] font-sans bg-[#D4AF37]/10 px-1.5 py-0.5 rounded-sm flex items-center gap-0.5">
                            <Brain className="w-2.5 h-2.5" /> {DOC_TYPE_LABELS[doc.ai_classification] || doc.ai_classification}
                            {doc.ai_confidence && <span className="text-white/30 ml-0.5">{Math.round(doc.ai_confidence * 100)}%</span>}
                          </span>
                        </>
                      )}
                      {doc.description && (
                        <>
                          <span className="text-white/20">|</span>
                          <span className="text-white/40 text-[10px] font-sans">{doc.description}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!doc.ai_classification && (
                    <button data-testid={`classify-doc-${doc.document_id}`} onClick={() => classifyDocument(doc.document_id)} className="w-8 h-8 flex items-center justify-center text-white/30 hover:text-[#D4AF37] transition-colors" title="AI Classify">
                      {classifying === doc.document_id ? <Loader2 className="w-4 h-4 animate-spin text-[#D4AF37]" /> : <Brain className="w-4 h-4" />}
                    </button>
                  )}
                  <button data-testid={`download-doc-${doc.document_id}`} onClick={() => handleDownload(doc)} className="w-8 h-8 flex items-center justify-center text-white/30 hover:text-[#D4AF37] transition-colors">
                    <Download className="w-4 h-4" />
                  </button>
                  <button data-testid={`delete-doc-${doc.document_id}`} onClick={() => handleDelete(doc.document_id)} className="w-8 h-8 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Property</label>
              <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                <SelectTrigger data-testid="upload-property-select" className="bg-white/5 border-white/10 text-white rounded-sm">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  {properties.map((p) => (
                    <SelectItem key={p.property_id} value={p.property_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">File (Max 5MB)</label>
              <div className="relative">
                <input
                  data-testid="file-input"
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xlsx"
                  className="block w-full text-sm text-white/60 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border file:border-white/10 file:text-sm file:font-sans file:bg-white/5 file:text-white/60 hover:file:bg-white/10 cursor-pointer"
                />
              </div>
              {selectedFile && (
                <div className="mt-2 flex items-center gap-2 text-xs text-white/50 font-sans">
                  <FileText className="w-3 h-3" />
                  {selectedFile.name} ({formatSize(selectedFile.size)})
                  <button onClick={() => setSelectedFile(null)} className="text-white/30 hover:text-red-400"><X className="w-3 h-3" /></button>
                </div>
              )}
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Description</label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g., Sale deed, Title certificate..." className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <Button data-testid="submit-upload-btn" onClick={handleUpload} disabled={!selectedFile || !form.property_id || uploading} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans disabled:opacity-50">
              {uploading ? "Uploading..." : "Upload Document"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Intelligence Report */}
      <Dialog open={showIntelligence} onOpenChange={setShowIntelligence}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-xl">Document Intelligence Report</DialogTitle></DialogHeader>
          {intelligence && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-sans text-sm font-medium">{intelligence.property_name}</p>
                  <p className="text-white/40 text-xs font-sans">Lifecycle State {intelligence.lifecycle_state} | {intelligence.total_documents} docs uploaded</p>
                </div>
                <div className="text-right">
                  <p className="font-serif text-2xl" style={{ color: intelligence.completeness_pct >= 80 ? "#556B2F" : intelligence.completeness_pct >= 50 ? "#F59E0B" : "#ef4444" }}>
                    {intelligence.completeness_pct}%
                  </p>
                  <p className="text-white/30 text-[10px] font-sans">Completeness</p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${intelligence.completeness_pct}%`, backgroundColor: intelligence.completeness_pct >= 80 ? "#556B2F" : intelligence.completeness_pct >= 50 ? "#F59E0B" : "#ef4444" }} />
              </div>

              {/* Required Documents */}
              <div>
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-2">Required Documents</p>
                <div className="space-y-1.5">
                  {intelligence.required_documents?.map(doc => (
                    <div key={doc.type} className="flex items-center justify-between text-xs font-sans">
                      <span className="text-white/60">{doc.label}</span>
                      {doc.status === "found" ? (
                        <span className="text-[#556B2F] flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Found</span>
                      ) : (
                        <span className="text-[#F59E0B] flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Missing</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Missing Documents Alert */}
              {intelligence.missing_documents?.length > 0 && (
                <div className="bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-sm p-3">
                  <p className="text-[#F59E0B] text-xs font-sans font-medium mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {intelligence.missing_documents.length} Missing Document(s)
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {intelligence.missing_documents.map(d => (
                      <span key={d.type} className="text-[9px] px-2 py-0.5 rounded-sm bg-[#F59E0B]/10 text-[#F59E0B] font-sans">{d.label}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
